import type { DepreciationMethod, DepreciationStatus, ResidualDepreciationApplication } from '@libs/enums';
import type {
  DateRangeObject,
  ItemAttributes,
  ItemNames,
  NumericRangeObject,
  OrEmpty,
  StringRangeObject,
} from '@ps-smart-cp/ppfw-client';

import {
  Characters,
  DateRange,
  ItemName,
  Locale,
  Numeric,
  NumericRange,
  SingleDate,
  SingleValue,
  StringRange,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  // 償却方法
  depreciationMethod: OrEmpty<DepreciationMethod>;

  // 耐用年数
  life: string;

  // 償却月数
  depreciationMonths: string;

  // 残存率
  residualRate: string;

  // 残存償却
  residualDepreciation: string;

  // 帳簿種別
  bookType: string;

  // 取得価額
  acquisitionValue: string;

  // 期首帳簿価額
  bookValueBegin: string;

  // 除却帳簿価額
  bookValueRetire: string;

  // 増減帳簿価額
  increDecreBookValue: string;

  // 当月末帳簿価額
  bookValueAtEndOfMonth: string;

  // 償却率
  depreciationRate: string;

  // 残存価額
  residualValue: string;

  // 当期普通償却額
  depCrPeriodTotal: string;

  // 帳簿価額
  bookValue: string;

  // 償却開始年月日
  depreciationStartDate: Date | null;

  // 残存月数
  remainingMonths: string;

  // 償却計算基礎額
  depCalculationBasisAmount: string;

  // 改定取得価額
  revisedAcquisitionValue: string;

  // 残存償却開始年度
  resdDepStartYear: string;

  // 残存償却適用区分
  resdDprecApplyClass: OrEmpty<ResidualDepreciationApplication>;

  // 償却開始年月日(抽出条件)
  depreciationStartDateCondition: DateRangeObject;

  // 取得価額(抽出条件)
  acquisitionValueCondition: NumericRangeObject;

  // 償却状況区分(抽出条件)
  dprecStatusClassCondition: OrEmpty<DepreciationStatus>;

  // 耐用年数(抽出条件)
  lifeCondition: NumericRangeObject;

  // 償却月数(抽出条件)
  depreciationMonthsCondition: NumericRangeObject;

  // 残存償却開始年度(抽出条件)
  resdDepStartYearCondition: StringRangeObject;

  // 償却状況区分
  dprecStatusClass: OrEmpty<DepreciationStatus>;

  // 当期任意償却累計額
  accumulatedDepAmtAdjCrPeriod: string;

  // 当期普通償却累計額(前月累計)
  accumulatedDepAmtCrPeriodPreviousMonth: string;

  // 当期普通償却累計額(当月累計)
  accumulatedDepAmtCrPeriodCrMonth: string;

  // 前月末帳簿価額
  bookValuePreviousMonth: string;

  // 債務帳簿価額
  liabilityBookValue: string;

  // 期首債務帳簿価額
  liabilityBookValueBegin: string;

  // 原始取得価額
  originalAcquisitionVal: string;

  //  期首普通償却累計額
  accumulatedDepAmtBegin: string;

  // 減価償却累計額
  depAccumulatedDep: string;

  // 利息計算基礎額
  interestCalculationBasisAmt: string;

  // 利息率
  interestRate: string;
}

const attributes = {
  depreciationMethod: SingleValue.shortValue(),
  life: Numeric.of(0, 99, 0),
  depreciationMonths: Numeric.of(0, 9_999, 0),
  residualRate: Numeric.of(0, 1, 3),
  residualDepreciation: SingleValue.shortValue(),
  bookType: SingleValue.shortValue(true),
  acquisitionValue: Numeric.preset.currency(),
  bookValueBegin: Numeric.preset.currency(),
  bookValueRetire: Numeric.preset.currency(),
  increDecreBookValue: Numeric.preset.currency(),
  bookValueAtEndOfMonth: Numeric.preset.currency(),
  depreciationRate: Numeric.of(0, 1, 3),
  residualValue: Numeric.preset.currency(),
  depCrPeriodTotal: Numeric.preset.currency(),
  depreciationStartDate: SingleDate.ymd(),
  remainingMonths: Numeric.of(0, 9_999, 0),
  depCalculationBasisAmount: Numeric.preset.currency(),
  revisedAcquisitionValue: Numeric.preset.currency(),
  resdDepStartYear: Characters.preset.numerical({ maxLen: 4 }),
  resdDprecApplyClass: SingleValue.shortValue(),
  depreciationStartDateCondition: DateRange.ymd(),
  acquisitionValueCondition: NumericRange.of(0, 9_999_999_999_999, 5),
  dprecStatusClassCondition: SingleValue.shortValue(),
  lifeCondition: NumericRange.of(0, 99, 0),
  depreciationMonthsCondition: NumericRange.of(0, 9_999, 0),
  resdDepStartYearCondition: StringRange.of(
    Characters.preset.numerical({ maxLen: 4 }),
    Characters.preset.numerical({ maxLen: 4 }),
  ),
  dprecStatusClass: SingleValue.shortValue(),
  accumulatedDepAmtAdjCrPeriod: Characters.preset.freeFormat(),
  accumulatedDepAmtCrPeriodPreviousMonth: Characters.preset.freeFormat(),
  accumulatedDepAmtCrPeriodCrMonth: Characters.preset.freeFormat(),
  bookValuePreviousMonth: Characters.preset.freeFormat(),
  liabilityBookValue: Characters.preset.freeFormat(),
  liabilityBookValueBegin: Characters.preset.freeFormat(),
  originalAcquisitionVal: Characters.preset.freeFormat(),
  accumulatedDepAmtBegin: Characters.preset.freeFormat(),
  depAccumulatedDep: Characters.preset.freeFormat(),
  interestCalculationBasisAmt: Characters.preset.freeFormat(),
  interestRate: Characters.preset.freeFormat(),
} satisfies ItemAttributes<Schema>;

const names = {
  depreciationMethod: ItemName.of([Locale.ja, '償却方法'], [Locale.en, 'Depreciation Method']),
  life: ItemName.of([Locale.ja, '耐用年数'], [Locale.en, 'Life']),
  depreciationMonths: ItemName.of([Locale.ja, '償却月数'], [Locale.en, 'Depreciation Months']),
  residualRate: ItemName.of([Locale.ja, '残存率'], [Locale.en, 'Residual Rate']),
  residualDepreciation: ItemName.of([Locale.ja, '残存償却'], [Locale.en, 'Residual Depreciation']),
  bookType: ItemName.of([Locale.ja, '帳簿種別'], [Locale.en, 'Book Classification']),
  acquisitionValue: ItemName.of([Locale.ja, '取得価額']),
  bookValueBegin: ItemName.of([Locale.ja, '期首帳簿価額']),
  bookValueRetire: ItemName.of([Locale.ja, '除却帳簿価額']),
  increDecreBookValue: ItemName.of([Locale.ja, '増減帳簿価額']),
  bookValueAtEndOfMonth: ItemName.of([Locale.ja, '当月末帳簿価額']),
  depreciationRate: ItemName.of([Locale.ja, '償却率']),
  residualValue: ItemName.of([Locale.ja, '残存価額']),
  depCrPeriodTotal: ItemName.of([Locale.ja, '当期普通償却額']),
  bookValue: ItemName.of([Locale.ja, '帳簿価額'], [Locale.en, 'Book Value']),
  depreciationStartDate: ItemName.of([Locale.ja, '償却開始年月日']),
  remainingMonths: ItemName.of([Locale.ja, '残存月数']),
  depCalculationBasisAmount: ItemName.of([Locale.ja, '償却計算基礎額']),
  revisedAcquisitionValue: ItemName.of([Locale.ja, '改定取得価額']),
  resdDepStartYear: ItemName.of([Locale.ja, '残存償却開始年度']),
  resdDprecApplyClass: ItemName.of([Locale.ja, '残存償却適用区分']),
  depreciationStartDateCondition: ItemName.of([Locale.ja, '償却開始年月日']),
  acquisitionValueCondition: ItemName.of([Locale.ja, '取得価額']),
  dprecStatusClassCondition: ItemName.of([Locale.ja, '償却状況区分']),
  lifeCondition: ItemName.of([Locale.ja, '耐用年数']),
  depreciationMonthsCondition: ItemName.of([Locale.ja, '償却月数']),
  resdDepStartYearCondition: ItemName.of([Locale.ja, '残存償却開始年度']),
  dprecStatusClass: ItemName.of([Locale.ja, '償却状況区分']),
  accumulatedDepAmtAdjCrPeriod: ItemName.of([Locale.ja, '当期任意償却累計額']),
  accumulatedDepAmtCrPeriodPreviousMonth: ItemName.of([Locale.ja, '当期普通償却累計額(前月累計)']),
  accumulatedDepAmtCrPeriodCrMonth: ItemName.of([Locale.ja, '当期普通償却累計額(当月累計)']),
  bookValuePreviousMonth: ItemName.of([Locale.ja, '前月末帳簿価額']),
  liabilityBookValue: ItemName.of([Locale.ja, '債務帳簿価額']),
  liabilityBookValueBegin: ItemName.of([Locale.ja, '期首債務帳簿価額']),
  originalAcquisitionVal: ItemName.of([Locale.ja, '原始取得価額']),
  accumulatedDepAmtBegin: ItemName.of([Locale.ja, '期首普通償却累計額']),
  depAccumulatedDep: ItemName.of([Locale.ja, '減価償却累計額']),
  interestCalculationBasisAmt: ItemName.of([Locale.ja, '利息計算基礎額']),
  interestRate: ItemName.of([Locale.ja, '利息率']),
} satisfies ItemNames<Schema>;

const labelNames = {
  toPerBookManagementItems: ItemName.of([Locale.ja, '帳簿別管理項目へ']),
  perBookManagementItems: ItemName.of([Locale.ja, '帳簿別管理項目']),
};

interface Record {
  depreciationMethod: string;
  life: string;
  depreciationMonths: string;
  residualRate: string;
  bookType: string;
  residualDepreciation: string;
  acquisitionValue: string;
  bookValueBegin: string;
  bookValueRetire: string;
  increDecreBookValue: string;
  bookValueAtEndOfMonth: string;
  depreciationRate: string;
  residualValue: string;
  depCrPeriodTotal: string;
  bookValue: string;
  depreciationStartDate: string;
  remainingMonths: string;
  depCalculationBasisAmount: string;
  revisedAcquisitionValue: string;
  resdDepStartYear: string;
  resdDprecApplyClass: string;
  depreciationStartDateFrom: string;
  depreciationStartDateTo: string;
  acquisitionValueFrom: string;
  acquisitionValueTo: string;
  dprecStatusClassCondition: string;
  lifeFrom: string;
  lifeTo: string;
  depreciationMonthsFrom: string;
  depreciationMonthsTo: string;
  resdDepStartYearFrom: string;
  resdDepStartYearTo: string;
  dprecStatusClass: string;
  accumulatedDepAmtAdjCrPeriod: string;
  accumulatedDepAmtCrPeriodPreviousMonth: string;
  accumulatedDepAmtCrPeriodCrMonth: string;
  bookValuePreviousMonth: string;
  liabilityBookValue: string;
  liabilityBookValueBegin: string;
  originalAcquisitionVal: string;
  accumulatedDepAmtBegin: string;
  depAccumulatedDep: string;
  interestCalculationBasisAmt: string;
  interestRate: string;
}

const bookInfo = {
  names,
  labelNames,
  attributes,
};

interface BookInfo {
  schema: Schema;
  record: Record;
}

export type { BookInfo };
export { bookInfo };
